// TODO: make type safe
import React, { useState } from 'react';
import { blokToComponent } from '../../helpers/blok-to-component';
import { MathService } from '../../services/math';
import { RocheUISlideAnimation, RocheUISlider } from '@repo/roche-ui';
import { StringService } from '../../services/string';
import { useTranslation } from '../../hooks/use-translation';
import { trackRocheSlide } from '../../helpers/roche-slider-tracking';
import { StoryblokService } from '../../services/storyblok';

enum SlidesPerPage {
  MINIMUM = 1,
  MAXIMUM = 3,
}

export interface TeaserSlider {
  teasers: any[];
  teasers_per_slider_page: number;
}

enum RocheTeaserSliderChildTypes {
  ROCHE_TEASER_AUTO = 'roche-teaser-auto',
  ROCHE_TEASER_EXTERNAL = 'roche-teaser-external',
  ROCHE_STORY_TEASER_AUTO = 'roche-story-teaser-auto',
  ROCHE_STORY_TEASER = 'roche-story-teaser',
  ROCHE_HERO_TEASER = 'roche-hero-teaser',
}

const RocheTeaserSliderAutoChildTypes = [
  RocheTeaserSliderChildTypes.ROCHE_TEASER_AUTO,
  RocheTeaserSliderChildTypes.ROCHE_STORY_TEASER_AUTO,
];

export const RocheTeaserSlider = ({ blok, getComponent }: any): JSX.Element => {
  const [childReadyCount, setChildReadyCount] = useState(0);
  // even in live-mode we get the unpublished teaser, ideally this would only get the published teasers
  // when migrating the roche-slider, consider using the swiper update method
  // when the number of slides (childReadyCount) changes
  // https://swiperjs.com/swiper-api#method-swiper-update
  const { teasers_per_slider_page: teasersPerSliderPage, teasers, slot } = blok as any;
  const slidesPerPageParsed = MathService.clamp(
    Number(teasersPerSliderPage),
    SlidesPerPage.MINIMUM,
    SlidesPerPage.MAXIMUM,
  );

  const listHasHeroTeasers = teasers.some(
    (teaser): boolean => teaser.component === RocheTeaserSliderChildTypes.ROCHE_HERO_TEASER,
  );
  const listHasAutoTeasers = teasers.some((teaser): boolean =>
    RocheTeaserSliderAutoChildTypes.includes(teaser.component),
  );

  // eslint-disable-next-line no-underscore-dangle
  const id = blok._uid || StringService.generate();
  const slidesPerPage = listHasHeroTeasers ? SlidesPerPage.MINIMUM : slidesPerPageParsed;
  const nextSlideLabel = useTranslation('next slide label');
  const prevSlideLabel = useTranslation('previous slide label');

  // Filter out any "unpublished" teaser, skip if we don't find preRenderContent
  // Special case: ROCHE_TEASER_EXTERNAL does not have preRenderContent in the model

  const getStaticTeasers = (teasers: any[]) => {
    if (StoryblokService.isInEditor()) {
      return teasers;
    }
    return teasers.filter(
      (teaser) =>
        teaser?.story?.story?.preRenderContent ||
        teaser?.page?.story?.preRenderContent ||
        teaser.component === RocheTeaserSliderChildTypes.ROCHE_TEASER_EXTERNAL,
    );
  };

  // Here we still use the old implementation for the "auto" types: roche-story-teaser-auto and roche-teaser-auto
  return listHasAutoTeasers ? (
    <roche-slider
      disable-auto-init={childReadyCount < teasers.length}
      in-app-with-virtualised-dom={true}
      loop={false}
      slides-per-page={slidesPerPage}
      transition='by-page'
      slot={slot}
    >
      <div>
        {teasers.map((teaser, index) =>
          blokToComponent(
            {
              blok: teaser,
              getComponent,
              updateHoistedState: (rendered: number) => setChildReadyCount((prevState) => prevState + rendered),
              wrapperTag: 'roche-slider-item',
              keyForChildren: index,
            },
            'slide',
          ),
        )}
      </div>
    </roche-slider>
  ) : (
    <RocheUISlider
      id={id}
      slidesPerPage={slidesPerPage}
      nextSlideLabel={nextSlideLabel}
      prevSlideLabel={prevSlideLabel}
      animation={RocheUISlideAnimation.ENTER_FROM_RIGHT}
      trackSlide={trackRocheSlide}
      slot={slot}
    >
      {getStaticTeasers(teasers).map((teaser, index) =>
        blokToComponent(
          {
            blok: teaser,
            getComponent,
            keyForChildren: index,
          },
          'teaser-slide',
        ),
      )}
    </RocheUISlider>
  );
};
